import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Globe from "./Globe";
import { MaskLine, EASE } from "./Reveal";
import { scrollToId } from "../../lib/scroll";
import { IMAGES } from "../../data/content";

export default function Hero() {
    const ref = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 60, damping: 18 });
    const sy = useSpring(my, { stiffness: 60, damping: 18 });
    const rotateX = useTransform(sy, [-0.5, 0.5], [5, -5]);
    const rotateY = useTransform(sx, [-0.5, 0.5], [-5, 5]);

    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yPhoto1 = useTransform(scrollYProgress, [0, 1], [0, -70]);
    const yPhoto2 = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const globeY = useTransform(scrollYProgress, [0, 1], [0, 40]);

    const onMouseMove = (e) => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            id="home"
            ref={ref}
            onMouseMove={onMouseMove}
            className="relative overflow-hidden bg-navy-deep pt-[76px]"
            data-testid="hero-section"
        >
            <div className="hero-grid pointer-events-none absolute inset-0" aria-hidden="true" />
            <div
                className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full"
                style={{ background: "radial-gradient(circle, rgba(197,160,89,0.12) 0%, transparent 65%)" }}
                aria-hidden="true"
            />

            <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-14 px-5 pb-24 pt-16 sm:pt-20 lg:grid-cols-12 lg:gap-6 lg:px-10 lg:pb-32 lg:pt-24">
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
                        className="flex items-center gap-4"
                        data-testid="hero-kicker"
                    >
                        <span className="h-px w-12 bg-gold" aria-hidden="true" />
                        <span className="text-[11px] font-semibold uppercase tracking-[0.34em] text-gold sm:text-xs">
                            Registered in Mauritius &bull; Connected Worldwide
                        </span>
                    </motion.div>

                    <h1 className="mt-8 font-serif text-4xl font-light leading-[1.05] tracking-tight text-ivory sm:text-5xl lg:text-6xl xl:text-7xl">
                        <MaskLine delay={0.25}>Global Connections.</MaskLine>
                        <MaskLine delay={0.42}>
                            <span>
                                Specialist{" "}
                                <em className="font-medium italic text-gold">Support.</em>
                            </span>
                        </MaskLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.85, delay: 0.65, ease: EASE }}
                        className="mt-7 max-w-xl text-base leading-relaxed text-slate-300/90 md:text-lg"
                        data-testid="hero-intro"
                    >
                        Viet Medica Ltd is a Mauritius-registered international group connecting
                        clients with healthcare, medical tourism, travel support and global trading
                        opportunities across Asia, Africa, the Middle East, Europe and wider
                        international markets.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.85, delay: 0.8, ease: EASE }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            type="button"
                            data-testid="hero-explore-healthcare-button"
                            onClick={() => scrollToId("#healthcare")}
                            className="group inline-flex items-center gap-3 bg-gold px-7 py-4 text-xs font-bold uppercase tracking-[0.18em] text-navy-deep transition-colors duration-300 hover:bg-gold-bright"
                        >
                            Explore Healthcare
                            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                        <button
                            type="button"
                            data-testid="hero-explore-trading-button"
                            onClick={() => scrollToId("#trading")}
                            className="group inline-flex items-center gap-3 border border-white/25 px-7 py-4 text-xs font-bold uppercase tracking-[0.18em] text-ivory transition-colors duration-300 hover:border-gold hover:text-gold"
                        >
                            Explore International Trading
                            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.05 }}
                        className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3"
                        data-testid="hero-trust-strip"
                    >
                        {["Two Specialist Divisions", "Seven International Brands", "Five Core Markets"].map((t) => (
                            <span key={t} className="flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                                <span className="h-1 w-1 rotate-45 bg-gold" aria-hidden="true" />
                                {t}
                            </span>
                        ))}
                    </motion.div>
                </div>

                <div className="relative lg:col-span-5" style={{ perspective: 1200 }}>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.94 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.2, delay: 0.35, ease: EASE }}
                        style={{ rotateX, rotateY, y: globeY, transformStyle: "preserve-3d" }}
                        className="relative mx-auto max-w-[520px]"
                    >
                        <motion.div
                            animate={{ y: [0, -12, 0] }}
                            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                        >
                            <Globe className="h-auto w-full" />
                        </motion.div>

                        <motion.figure
                            style={{ y: yPhoto1 }}
                            className="absolute -left-6 top-6 hidden w-36 border border-gold/30 bg-navy-surface p-1.5 shadow-2xl shadow-black/60 lg:block xl:-left-14 xl:w-44"
                        >
                            <img src={IMAGES.surgery} alt="Surgical team providing specialist care" className="h-24 w-full object-cover xl:h-28" loading="lazy" />
                            <figcaption className="px-1 pb-1 pt-2 text-[9px] font-semibold uppercase tracking-[0.28em] text-gold">
                                Patient Care
                            </figcaption>
                        </motion.figure>

                        <motion.figure
                            style={{ y: yPhoto2 }}
                            className="absolute -right-4 bottom-10 hidden w-36 border border-gold/30 bg-navy-surface p-1.5 shadow-2xl shadow-black/60 lg:block xl:-right-10 xl:w-44"
                        >
                            <img src={IMAGES.containers} alt="Container port supporting global trade" className="h-24 w-full object-cover xl:h-28" loading="lazy" />
                            <figcaption className="px-1 pb-1 pt-2 text-[9px] font-semibold uppercase tracking-[0.28em] text-gold">
                                Global Trade
                            </figcaption>
                        </motion.figure>
                    </motion.div>
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 1 }}
                className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex"
                aria-hidden="true"
            >
                <span className="text-[10px] font-semibold uppercase tracking-[0.4em] text-slate-500">Scroll</span>
                <motion.span
                    animate={{ scaleY: [1, 0.3, 1], opacity: [1, 0.4, 1] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                    className="block h-10 w-px origin-top bg-gold/70"
                />
            </motion.div>
        </section>
    );
}
